import React from "react";
import { Popover } from "@mui/material";
import { Link } from "react-router-dom";

/**
 * Professional mega-menu dropdown for the modern top nav.
 * Renders one or more sections (each: optional uppercase header + a grid of
 * clean text item-rows) inside a rounded, shadowed light panel. Big sections
 * auto-flow into 2–3 columns. Built on Popover so we control the layout
 * instead of being forced into MUI's vertical MenuItem list.
 *
 * Item rows are real router <Link> anchors, so the browser's native
 * "Open in New Tab" (right-click / middle-click / Ctrl+click) works — a plain
 * left-click still does in-app SPA navigation and closes the panel.
 *
 * Props:
 *   anchorEl, open, onClose
 *   sections: [{ key, title?, items: [{ title, path }] }]
 *   currentPath: string   (drives the active-row highlight)
 *   align: "left" | "right"
 */
const colCount = (n) => (n <= 6 ? 1 : n <= 12 ? 2 : 3);

const MenuPanel = ({ anchorEl, open, onClose, sections = [], currentPath = "", align = "left" }) => {
  return (
    <Popover
      anchorEl={anchorEl}
      open={open}
      onClose={onClose}
      anchorOrigin={{ vertical: "bottom", horizontal: align }}
      transformOrigin={{ vertical: "top", horizontal: align }}
      slotProps={{
        paper: {
          sx: {
            mt: 0.75,
            borderRadius: "12px",
            border: "1px solid #eef2f7",
            boxShadow: "var(--shadow-lg)",
            maxWidth: "92vw",
            maxHeight: "70vh",
            overflowY: "auto",
          },
        },
      }}
    >
      <div className="py-1.5">
        {sections.map((section, si) => {
          const items = section.items || [];
          const cols = colCount(items.length);
          return (
            <div
              key={section.key || si}
              className={si > 0 ? "mt-1 pt-1.5 border-t border-gray-100" : ""}
            >
              {section.title && (
                <div className="px-3 pt-1 pb-1 text-[11px] font-bold uppercase tracking-wider text-slate-400">
                  {section.title}
                </div>
              )}
              <div
                className="grid gap-x-2 px-1.5"
                style={{ gridTemplateColumns: `repeat(${cols}, minmax(190px, 1fr))` }}
              >
                {items.map((item) => {
                  const active = item.path && currentPath.includes(item.path);
                  return (
                    // Real <a href> (via router Link) so the browser offers its
                    // native "Open in New Tab" (right-click / middle-click /
                    // Ctrl+click). Plain left-click still does SPA navigation.
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={() => onClose?.()}
                      className={`flex items-center text-start text-[13.5px] rounded-[8px] px-3 py-1.5 border-s-2 transition-colors no-underline ${
                        active
                          ? "bg-primary-50 text-primary font-semibold border-primary"
                          : "text-slate-600 border-transparent hover:bg-primary-50 hover:text-primary"
                      }`}
                    >
                      {item.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
    </Popover>
  );
};

export default MenuPanel;
